import {
  first_value,
  normalized,
  text
} from './company_portfolio_common';
import { get_record_status, is_open_case_status } from '../CompanyCases/company_cases_common';

const FLAGGED_STATES = ['true', 'flagged', 'yes', 'suspected'];
const DOMAIN_CLASSES = new Map([
  ['claims', 'is-claims'],
  ['legal', 'is-legal'],
  ['service', 'is-service'],
  ['oversight', 'is-oversight'],
  ['hr', 'is-hr'],
  ['safety', 'is-safety']
]);

function object_text(value) {
  if (!value || typeof value !== 'object') return text(value);
  return text(first_value(value, ['name', 'status', 'entity_type_name']) || value.label);
}

export function status_text(record = {}) {
  return object_text(first_value(record, [
    'application_status_name', 'current_status_name', 'workflow_status_name',
    'status_name', 'application_status', 'current_status', 'status'
  ]));
}

export function is_active_case(record = {}) {
  return is_open_case_status(get_record_status(record));
}

export function risk_score(record = {}) {
  const score = Number(first_value(record, ['total_score', 'risk_score', 'score', 'risk']));
  return Number.isFinite(score) ? score : 0;
}

export function is_high_risk(record = {}) {
  const label = normalized(first_value(record, [
    'risk_level', 'risk_label', 'risk_band', 'risk_category'
  ]));
  return label.includes('high') || risk_score(record) >= 70;
}

function parse_hours_left(value) {
  const label = text(value).toLowerCase();
  const days_match = label.match(/(\d+(?:\.\d+)?)\s*d/);
  const days = days_match ? Number(days_match[1]) : 0;
  const marker = label.indexOf('h');
  if (marker < 0) return days ? days * 24 : null;
  const prefix = label.slice(0, marker).trim();
  const start = Math.max(prefix.lastIndexOf(' '), prefix.lastIndexOf(':')) + 1;
  const hours = Number(prefix.slice(start));
  return Number.isFinite(hours) ? (days * 24) + hours : null;
}

function due_date(record) {
  const value = first_value(record, [
    'sla_due_at', 'sla_due_date', 'due_at', 'due_date', 'deadline'
  ]);
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

export function is_sla_at_risk(record = {}) {
  const raw_sla = first_value(record, [
    'sla',
    'sla_status',
    'sla_label',
    'due_status',
    'sla_class',
    'sla_runtime_status',
    'sla_runtime_status_label',
    'sla_runtime_badge_label'
  ]);
  const sla = normalized(raw_sla);
  if (['overdue', 'breach', 'breached', 'escalated', 'at-risk', 'warning'].some((flag) => sla.includes(flag))) return true;
  const hours = parse_hours_left(raw_sla);
  if (hours !== null) return hours <= 168;
  const date = due_date(record);
  return date ? date.getTime() - Date.now() <= 7 * 24 * 60 * 60 * 1000 : false;
}

function explicit_leakage(record) {
  return record.leakage_flag === true
    || record.is_leakage_flagged === true
    || record.fraud_flag === true
    || record.is_fraud_suspected === true;
}

function flagged_leakage(record) {
  const state = normalized(first_value(record, [
    'leakage_status', 'leakage_flag', 'is_leakage_flagged',
    'fraud_flag', 'is_fraud_suspected'
  ]));
  return FLAGGED_STATES.some((flag) => state.includes(flag));
}

function has_leakage(record) {
  return explicit_leakage(record) || flagged_leakage(record);
}

export function leakage_amount(record = {}) {
  if (!has_leakage(record)) return 0;
  const value = first_value(record, [
    'leakage_amount', 'flagged_amount', 'potential_leakage',
    'claim_value', 'amount', 'value'
  ]);
  if (typeof value === 'number') return finite_amount(value);
  const amount = Number(text(value).replace(/[^0-9.-]/g, ''));
  return finite_amount(amount);
}

function finite_amount(value) {
  return Number.isFinite(value) ? value : 0;
}

export function created_date(record = {}) {
  const value = first_value(record, [
    'date_created', 'created_at', 'application_date_created', 'opened_at', 'submitted_on'
  ]);
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

export function domain_name(record = {}) {
  return object_text(first_value(record, [
    'entity_type_name', 'entity_type_label', 'domain_name', 'domain'
  ]));
}

export function domain_badge_class(label = '') {
  return DOMAIN_CLASSES.get(normalized(label)) || 'is-default';
}

export function case_code(record = {}) {
  const explicit = text(first_value(record, ['case_code', 'reference', 'code']));
  if (explicit) return explicit;
  const application_id = Number(first_value(record, [
    'application_id', 'latest_application_id', 'current_application_id'
  ]));
  return application_id
    ? `C-00${application_id}`
    : `EP-${first_value(record, ['id', 'entity_profile_id'])}`;
}

export function case_title(record = {}) {
  return text(first_value(record, [
    'entity_name', 'profile_name', 'name', 'title', 'description'
  ])) || `${domain_name(record) || 'Case'} case`;
}

export function sla_label(record = {}) {
  return text(first_value(record, ['sla', 'sla_status', 'sla_label', 'due_status'])) || 'On track';
}

export function role_name(role = {}) {
  return text(role.role || role.role_name || role.name);
}

export function format_currency(amount) {
  if (!amount) return 'R 0';
  if (amount >= 1000000) return format_scaled_currency(amount, 1000000, 10000000, 'M');
  if (amount >= 1000) return format_scaled_currency(amount, 1000, 100000, 'K');
  return `R ${Math.round(amount).toLocaleString('en-ZA')}`;
}

function format_scaled_currency(amount, divisor, rounding_threshold, suffix) {
  const decimals = amount >= rounding_threshold ? 0 : 1;
  return `R ${(amount / divisor).toFixed(decimals)}${suffix}`;
}
